import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import "../styles/Navbar.css";

const menuVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const sidebarVariants = {
  hidden: { x: "100%" },
  visible: { x: 0, transition: { type: "tween", duration: 0.4 } },
  exit: { x: "100%", transition: { type: "tween", duration: 0.3 } },
};

// ... imports remain the same

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // "ventures" | "about" | null

  const venturesRef = useRef(null);
  const aboutRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openDropdown === "ventures" &&
        venturesRef.current &&
        !venturesRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
      if (
        openDropdown === "about" &&
        aboutRef.current &&
        !aboutRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
      if (
        menuOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown, menuOpen]);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  return (
    <>
      <motion.nav
        className={`navbar ${scrolled ? "scrolled" : ""}`}
        initial="hidden"
        animate="visible"
        variants={menuVariants}
      >
        {" "}
        <Link to="/">
          <img src="/logo.png" alt="Logo" className="logo" />
        </Link>
        <ul className="nav-links">
          <li>
            <Link to="/">Home</Link>
          </li>

          <li
            className={`dropdown ${openDropdown === "ventures" ? "open" : ""}`}
            ref={venturesRef}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setOpenDropdown((prev) =>
                  prev === "ventures" ? null : "ventures"
                );
              }}
            >
              Our Ventures <ChevronDown size={14} />
            </a>
            <div className="dropdown-menu">
              <Link to="/partners">Nexa Partners</Link>
              <Link to="/accelerator">Nexa Accelerate</Link>
              <Link to="/elevate">Nexa Elevate</Link>
            </div>
          </li>

          <li
            className={`dropdown ${openDropdown === "about" ? "open" : ""}`}
            ref={aboutRef}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setOpenDropdown((prev) => (prev === "about" ? null : "about"));
              }}
            >
              About <ChevronDown size={14} />
            </a>
            <div className="dropdown-menu">
              <Link to="/about">Who we are?</Link>
              <Link to="/team">Team </Link>
              <Link to="/careers">Careers</Link>
            </div>
          </li>

          <li>
            <Link to="/blog">Blog</Link>
          </li>
          <li>
            <Link to="/contact">Contact</Link>
          </li>
        </ul>
        {menuOpen ? (
          <X className="mobile-menu-icon" onClick={toggleMenu} />
        ) : (
          <Menu className="mobile-menu-icon" onClick={toggleMenu} />
        )}
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="sidebar-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="mobile-sidebar open"
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              ref={sidebarRef}
            >
              <X className="sidebar-close-icon" onClick={toggleMenu} />
              <ul>
                <li>
                  <Link to="/" onClick={toggleMenu}>
                    Home
                  </Link>
                </li>
                <li className="dropdown">
                  <span>
                    Our Ventures <ChevronDown size={14} />
                  </span>
                  <div className="dropdown-menu">
                    <Link to="/partners" onClick={toggleMenu}>
                      Nexa Partners
                    </Link>
                    <Link to="/accelerator" onClick={toggleMenu}>
                      Nexa Accelerate{" "}
                    </Link>
                    <Link to="/elevate" onClick={toggleMenu}>
                      Nexa Elevate
                    </Link>
                  </div>
                </li>
                <li className="dropdown">
                  <span>
                    About <ChevronDown size={14} />
                  </span>
                  <div className="dropdown-menu">
                    <Link to="/about" onClick={toggleMenu}>
                      Who we are?
                    </Link>
                    <Link to="/team" onClick={toggleMenu}>
                      Team
                    </Link>
                    <Link to="/careers" onClick={toggleMenu}>
                      Careers
                    </Link>
                  </div>
                </li>
                <li>
                  <Link to="/blog" onClick={toggleMenu}>
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/contact" onClick={toggleMenu}>
                    Contact
                  </Link>
                </li>
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
